import React, { useCallback, useMemo } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { AblEnforcementDialog } from '@bingads-webui-cc-react/abl-enforcement';
import { useValidationContext } from '../validation-context';
import { useDataManagement, useErrorHandler } from '../../hooks';
import { AddressSelectorOptions, Forms, skipValidationResult } from '../../constants';
import { areAddressesEqual } from '../../utils';

export const AddressValidation = () => {
  const {
    i18n,
    formControls,
    getAblInfoData,
    dataManagement,
    notificationApi,
    onCancel,
    onContinue,
    setAddressData,
    setPrefillAddressId,
  } = useValidationContext();

  const {
    addressData: {
      address,
    },
  } = getAblInfoData();

  const isABLValidated = useMemo(() => _.isBoolean(_.get(address, 'IsValid')), [address]);

  const { data: businessAddressValidationResult, error, loading } = useDataManagement({
    dataFetch: jsdata => (isABLValidated || formControls[Forms.AddressForm].optional ? Promise.resolve(skipValidationResult) : jsdata.VerifyBusinessAddress.create(address)),
    ...dataManagement.verifyBusinessAddress,
  }, []);

  const updateABLAfterValidation = useCallback((selectedAddress) => {
    setAddressData(({ address: prevAddress, addressWithValidation }) => ({
      address: selectedAddress,
      addressWithValidation: areAddressesEqual(prevAddress, selectedAddress) ? addressWithValidation : null,
      isValid: true,
    }));
    onContinue();
  }, [onContinue, setAddressData]);

  const successCallback = useCallback(({ data: validationResult }) => {
    if (validationResult === skipValidationResult) {
      onContinue();
    } else if (_.get(validationResult, 'IsValid')) {
      updateABLAfterValidation(validationResult.OriginalAddress);
    }
  }, [onContinue, updateABLAfterValidation]);

  useErrorHandler({
    data: businessAddressValidationResult,
    error,
    loading,
    notificationApi,
    errorCallback: onCancel,
    successCallback,
  });

  const onApplyAction = useCallback((selectedAddress) => {
    // If the new selected address is from addressList (has Id), we keep the Id which can avoid open the AddressForm
    // If the new selected address is from suggestions or original enterNewAddress (has no Id), we make sure the AddressForm will be open by setting enterNewAddressId
    setPrefillAddressId(selectedAddress.Id || AddressSelectorOptions.enterNewAddressId);
    updateABLAfterValidation(selectedAddress);
  }, [setPrefillAddressId, updateABLAfterValidation]);

  const { IsValid, OriginalAddress, AddressSuggestions } = businessAddressValidationResult || {};

  return IsValid === false
    ? (
      <AblEnforcementDialog
        id="address-validation-abl-enforcement-dialog"
        i18n={i18n}
        country={address.Country}
        originalAddress={OriginalAddress}
        addressSuggestions={AddressSuggestions}
        onApplyAction={onApplyAction}
        onCancelAction={onCancel}
        showDialog
      />
    )
    : null;
};
